import React, { useEffect, useState } from "react";
import ConfettiExplosion from "react-confetti-explosion";
import { Modal, ModalHeader, ModalContent, ModalBasicLayout } from "@vibe/core/next";
import { SharedColumnsType } from "../Tour/CustomTourComponent";
import { getColumnIcon, getColumnBgColor } from "@utils/columnIcons";
import { MdCelebration } from "react-icons/md";

interface ConfettiModalProps {
  boardName: string;
  tourComplete: boolean;
  handleCloseTour: () => void;
  sharedColumnsTour: SharedColumnsType[];
}

const ConfettiModalHeader = ({ boardName }: { boardName: string }) => {
  return (
    <div id="confetti-header" className="flex flex-col items-center pt-8">
      <div className="justify-center items-center w-24 h-24 rounded-full mb-6 flex bg-[#4744e51a]">
        <MdCelebration size={48} color="#4744e5" />
      </div>
      <h1 className="mb-2">Congratulations!</h1>
      <p>
        Your item is now also in <b>{boardName}</b>!
      </p>
    </div>
  );
};

const ConfettiModalContent = ({ sharedColumns }: { sharedColumns: SharedColumnsType[] }) => {
  return (
    <>
      <div className="p-6 bg-[#f5f7fb] rounded-md mb-8">
        <h2 className="!mb-4 text-sm font-semibold tracking-wider uppercase text-on-surface-variant">
          Your shared columns are:
        </h2>

        {sharedColumns && sharedColumns.length > 0 && (
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            {sharedColumns.map((col) => (
              <div className="flex gap-3 items-center p-3 bg-white rounded-lg border border-transparent transition-all cursor-default hover:border-outline-variant/30 group">
                <div
                  className={`flex justify-center items-center p-1.5 rounded ${getColumnBgColor(col.type)}`}
                >
                  {getColumnIcon(col.type, 16, "white")}
                </div>
                <span className="text-sm font-medium text-on-surface">{col.title}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </>
  );
};

const ConfettiModalFooter = () => (
  <div className="sticky bottom-0 px-6 py-6 bg-white border-t border-outline-variant/20">
    <p className="text-sm text-center text-light-secondary-text-color text-pretty">
      When columns have the same name across boards, they sync.{" "}
      <a
        rel="noopener noreferrer"
        target="_blank"
        href="https://sameitem.pioneera.io/en/article/getting-started-with-same-item-multiple-boards-n79d2s/#1-sharing-columns-across-boards"
      >
        Read more
      </a>
    </p>
  </div>
);

const ConfettiModal = ({
  boardName,
  tourComplete,
  handleCloseTour,
  sharedColumnsTour,
}: ConfettiModalProps) => {
  const [sharedColumns, setSharedColumns] = useState<SharedColumnsType[]>([]);

  useEffect(() => {
    if (sharedColumnsTour && sharedColumnsTour.length > 0) {
      setSharedColumns(sharedColumnsTour);
    }
  }, [sharedColumnsTour]);

  return (
    <div>
      <Modal id="modal-basic" onClose={handleCloseTour} show={tourComplete} size="medium">
        <ModalBasicLayout>
          <ModalHeader
            title={<ConfettiModalHeader boardName={boardName} />}
            className="flex !justify-center !items-center text-center p-0"
          />
          <ModalContent className="pt-2">
            {tourComplete && (
              <div className="flex justify-center items-center mx-auto">
                {/* TODO(human): Tune confetti animation — adjust duration, force, and particleCount */}
                <ConfettiExplosion
                  zIndex={9999999}
                  duration={3500}
                  force={0.4}
                  particleCount={80}
                />
              </div>
            )}
            <ConfettiModalContent sharedColumns={sharedColumns} />
          </ModalContent>
        </ModalBasicLayout>
        <ConfettiModalFooter />
      </Modal>
    </div>
  );
};

export default ConfettiModal;
